/* ==========================================================================
   ELVA — Above the fold
   Gallery paging, option selection, bundle selection, add-to-cart.
   No dependencies, no theme coupling.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (!format) return '$' + amount;
    // Shopify money formats use {{amount}} style placeholders.
    return format.replace(/\{\{\s*\w+\s*\}\}/, function (match) {
      if (/amount_no_decimals/.test(match)) return String(Math.round(cents / 100));
      if (/with_comma_separator/.test(match)) return amount.replace('.', ',');
      return amount;
    });
  };

  /* ------------------------------------------------------------- gallery */

  function Gallery(root) {
    var track = root.querySelector('[data-elva-track]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-elva-dot]'));
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    var current = 0;

    function setCurrent(index) {
      if (index === current) return;
      current = index;
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function nearestIndex() {
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      slides.forEach(function (slide, i) {
        var center = slide.offsetLeft + slide.offsetWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          setCurrent(nearestIndex());
          ticking = false;
        });
      },
      { passive: true }
    );

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var slide = slides[i];
        if (!slide) return;
        track.scrollTo({
          left: slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2,
          behavior: 'smooth'
        });
      });
    });

    // Jump to the slide bound to a variant when the variant changes.
    root.addEventListener('elva:media', function (event) {
      var mediaId = event.detail && event.detail.mediaId;
      if (!mediaId) return;
      var target = slides.filter(function (slide) {
        return slide.getAttribute('data-media-id') === String(mediaId);
      })[0];
      if (!target) return;
      track.scrollTo({
        left: target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2,
        behavior: 'smooth'
      });
    });
  }

  /* --------------------------------------------------------------- hero */

  function Hero(root) {
    var variants = [];
    var dataEl = root.querySelector('[data-elva-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    var form = root.querySelector('[data-elva-form]');
    var idInput = root.querySelector('[data-elva-variant-id]');
    var priceEls = Array.prototype.slice.call(root.querySelectorAll('[data-elva-price]'));
    var compareEls = Array.prototype.slice.call(
      root.querySelectorAll('[data-elva-compare]')
    );
    var buyBtn = root.querySelector('[data-elva-buy]');
    var buyLabel = root.querySelector('[data-elva-buy-label]');
    var format = root.getAttribute('data-money-format') || '';
    var soldOutText = root.getAttribute('data-sold-out') || 'Sold out';
    var buyText = buyLabel ? buyLabel.textContent : '';

    function selectedOptions() {
      var out = [];
      root.querySelectorAll('[data-elva-option]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-elva-option'), 10);
        var checked = group.querySelector('input:checked');
        out[index] = checked ? checked.value : null;
      });
      return out;
    }

    function findVariant() {
      var chosen = selectedOptions();
      if (!chosen.length) return variants[0] || null;
      return (
        variants.filter(function (variant) {
          return chosen.every(function (value, i) {
            return value == null || variant.options[i] === value;
          });
        })[0] || null
      );
    }

    function markAvailability() {
      // Grey out option values that lead to no purchasable variant.
      var chosen = selectedOptions();
      root.querySelectorAll('[data-elva-option]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-elva-option'), 10);
        group.querySelectorAll('input').forEach(function (input) {
          var probe = chosen.slice();
          probe[index] = input.value;
          var match = variants.filter(function (variant) {
            return probe.every(function (value, i) {
              return value == null || variant.options[i] === value;
            });
          })[0];
          var wrapper = input.closest('.elva-swatch, .elva-pill');
          if (!wrapper) return;
          wrapper.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    function render() {
      var variant = findVariant();

      if (idInput) idInput.value = variant ? variant.id : '';

      priceEls.forEach(function (el) {
        el.textContent = variant ? money(variant.price, format) : '';
      });

      compareEls.forEach(function (el) {
        var show =
          variant && variant.compare_at_price && variant.compare_at_price > variant.price;
        el.textContent = show ? money(variant.compare_at_price, format) : '';
        el.hidden = !show;
      });

      if (buyBtn) {
        var available = !!(variant && variant.available);
        buyBtn.disabled = !available;
        if (buyLabel) buyLabel.textContent = available ? buyText : soldOutText;
      }

      if (variant && variant.featured_media_id) {
        root.dispatchEvent(
          new CustomEvent('elva:media', {
            detail: { mediaId: variant.featured_media_id }
          })
        );
      }

      markAvailability();
    }

    root.querySelectorAll('[data-elva-option] input').forEach(function (input) {
      input.addEventListener('change', render);
    });

    /* ------------------------------------------------------------ bundles */

    var bundles = Array.prototype.slice.call(root.querySelectorAll('[data-elva-bundle]'));

    function renderBundle() {
      bundles.forEach(function (b) {
        var input = b.querySelector('input');
        b.classList.toggle('is-selected', !!(input && input.checked));
      });

      var checked = bundles.filter(function (b) {
        return b.querySelector('input').checked;
      })[0];
      if (!checked) return;

      var variantId = checked.getAttribute('data-variant-id');
      if (variantId && idInput) idInput.value = variantId;

      var price = checked.getAttribute('data-price');
      if (price) {
        priceEls.forEach(function (el) {
          el.textContent = price;
        });
      }
    }

    bundles.forEach(function (bundle) {
      var input = bundle.querySelector('input');
      if (input) input.addEventListener('change', renderBundle);
    });

    /* ---------------------------------------------------------- add to cart */

    if (form) {
      form.addEventListener('submit', function (event) {
        if (!window.fetch || form.getAttribute('data-elva-ajax') !== 'true') return;
        event.preventDefault();

        if (buyBtn) buyBtn.disabled = true;

        // Post the whole form so line item properties (upsell tick-boxes,
        // engraving fields, anything a merchant adds) survive the AJAX path.
        var payload = new FormData(form);
        payload.set('id', idInput.value);
        payload.set('quantity', '1');

        fetch(window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: payload
        })
          .then(function (response) {
            return response.json();
          })
          .then(function () {
            document.dispatchEvent(new CustomEvent('elva:added'));
            window.location.href =
              (window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root
                : '/') + 'cart';
          })
          .catch(function () {
            form.submit();
          })
          .finally(function () {
            if (buyBtn) buyBtn.disabled = false;
          });
      });
    }

    if (variants.length) render();
    if (bundles.length) renderBundle();
  }

  /* ------------------------------------------------- announcement countdown */

  function Countdown(el) {
    var deadlineAttr = el.getAttribute('data-deadline');
    var hoursAttr = parseFloat(el.getAttribute('data-hours') || '0');
    var storageKey = 'elva-countdown-' + (el.getAttribute('data-key') || 'default');

    var deadline;
    if (deadlineAttr) {
      deadline = new Date(deadlineAttr).getTime();
    } else if (hoursAttr > 0) {
      // Rolling per-visitor timer: starts on first view, persists across pages.
      var stored = null;
      try {
        stored = window.localStorage.getItem(storageKey);
      } catch (e) {
        stored = null;
      }
      var start = stored ? parseInt(stored, 10) : Date.now();
      if (!stored) {
        try {
          window.localStorage.setItem(storageKey, String(start));
        } catch (e) {
          /* private mode — timer simply restarts */
        }
      }
      deadline = start + hoursAttr * 3600 * 1000;
      if (deadline <= Date.now()) {
        deadline = Date.now() + hoursAttr * 3600 * 1000;
        try {
          window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {
          /* ignore */
        }
      }
    } else {
      return;
    }

    var parts = {
      hours: el.querySelector('[data-unit="hours"]'),
      minutes: el.querySelector('[data-unit="minutes"]'),
      seconds: el.querySelector('[data-unit="seconds"]')
    };

    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function tick() {
      var left = Math.max(0, deadline - Date.now());
      var totalSeconds = Math.floor(left / 1000);
      var h = Math.floor(totalSeconds / 3600);
      var m = Math.floor((totalSeconds % 3600) / 60);
      var s = totalSeconds % 60;

      if (parts.hours) parts.hours.textContent = pad(h);
      if (parts.minutes) parts.minutes.textContent = pad(m);
      if (parts.seconds) parts.seconds.textContent = pad(s);

      if (left <= 0) window.clearInterval(timer);
    }

    var timer = window.setInterval(tick, 1000);
    tick();
  }

  /* ----------------------------------------------------------------- init */

  function init(scope) {
    (scope || document).querySelectorAll('[data-elva-hero]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      Gallery(el);
      Hero(el);
    });

    (scope || document).querySelectorAll('[data-elva-countdown]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      Countdown(el);
    });

    (scope || document).querySelectorAll('[data-elva-dismiss]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      el.addEventListener('click', function () {
        var target = el.closest('[data-elva-bar]');
        if (target) target.hidden = true;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
